import { useState, useCallback, useRef, useEffect } from 'react';

interface PanState {
  isPanning: boolean;
  startX: number;
  startY: number;
  scrollLeft: number;
  scrollTop: number;
}

interface UsePanningProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  enabled?: boolean;
}

export const usePanning = ({ containerRef, enabled = true }: UsePanningProps) => {
  const [isPanning, setIsPanning] = useState(false);
  const panState = useRef<PanState>({
    isPanning: false,
    startX: 0,
    startY: 0,
    scrollLeft: 0,
    scrollTop: 0,
  });

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!enabled || !containerRef.current) return;

    // Only start panning if clicking on the container itself or its direct children (not nodes)
    const target = e.target as HTMLElement;

    // Check if clicking on a node or any interactive element
    if (target.closest('.todo-node-free-floating') ||
        target.closest('.archived-task-group') ||
        target.closest('.zoom-controls') ||
        target.closest('.mini-map') ||
        target.tagName === 'BUTTON' ||
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA') {
      return;
    }

    // Check if it's a middle mouse button or left button with space/shift key
    const isMiddleButton = e.button === 1;
    const isLeftWithModifier = e.button === 0 && e.shiftKey;
    const isLeftButton = e.button === 0;

    if (!isMiddleButton && !isLeftWithModifier && !isLeftButton) return;

    e.preventDefault();

    const container = containerRef.current;

    panState.current = {
      isPanning: true,
      startX: e.pageX,
      startY: e.pageY,
      scrollLeft: container.scrollLeft,
      scrollTop: container.scrollTop,
    };

    setIsPanning(true);

    // Add cursor style to body during pan
    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';
  }, [containerRef, enabled]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!panState.current.isPanning || !containerRef.current) return;

    e.preventDefault();

    const container = containerRef.current;
    const dx = e.pageX - panState.current.startX;
    const dy = e.pageY - panState.current.startY;

    // Invert the direction for more natural panning
    container.scrollLeft = panState.current.scrollLeft - dx;
    container.scrollTop = panState.current.scrollTop - dy;
  }, [containerRef]);

  const handleMouseUp = useCallback(() => {
    if (!panState.current.isPanning) return;

    panState.current.isPanning = false;
    setIsPanning(false);

    // Reset cursor
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  const handleMouseLeave = useCallback(() => {
    // Stop panning if mouse leaves the window
    if (panState.current.isPanning) {
      handleMouseUp();
    }
  }, [handleMouseUp]);

  // Set up global event listeners for mouse move and up
  useEffect(() => {
    if (!enabled) return;

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [handleMouseMove, handleMouseUp, handleMouseLeave, enabled]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, []);

  return {
    isPanning,
    handleMouseDown,
    panHandlers: {
      onMouseDown: handleMouseDown,
    }
  };
};